import React, { useState, useEffect } from 'react';
import Intake from './components/Intake';
import Generation from './components/Generation';
import Results from './components/Results';
import Dashboard from './components/Dashboard';
import Auth from './components/Auth';
import { supabase } from './supabase';

const ENGINE_URL = 'https://brandostro-engine-253902457893.us-central1.run.app';

// ═══════════════════════════════════════════════════════════════════════════
// BRIEF BUILDER HELPERS
// ═══════════════════════════════════════════════════════════════════════════
function buildAssessmentBrief(brief, name) {
  return [
    '╔══════════════════════════════════════════════════════════════════╗',
    '║  NAME ASSESSMENT MODE — CLIENT HAS PROVIDED THEIR OWN NAME       ║',
    `║  Client name: "${name}"`,
    '║  Score this name professionally. Build all phases around it.     ║',
    '╚══════════════════════════════════════════════════════════════════╝',
    '',
    `BRIEF: ${brief}`,
    '',
    'For phase02, provide:',
    `1. The client name "${name}" as names[0] with these additional fields:`,
    '   - assessmentScore: overall 0-100 score',
    '   - clarity: 0-100, culturalResonance: 0-100, trademarkRisk: 0-100 (higher = safer)',
    '   - domainAvailability: 0-100, memorability: 0-100, scalability: 0-100',
    '   - assessmentVerdict: one sentence professional verdict',
    '   - userProvided: true',
    '2. If assessmentScore < 75, generate 3 superior alternative names as names[1-3], each with:',
    '   - assessmentScore (must be higher than the client name)',
    '   - superiorReason: one sentence explaining why this name is stronger',
    '   - isAlternative: true',
    '3. If assessmentScore >= 75, names[0] only — validate with strength.',
    `ALL other phases must use "${name}" consistently throughout.`,
  ].join('\n');
}

function buildNameLockedBrief(brief, name) {
  return [
    '╔══════════════════════════════════════════════════════════════╗',
    '║  CONFIRMED BRAND NAME — NON-NEGOTIABLE — USE IN EVERY PHASE  ║',
    `║  "${name}"`,
    '║  This is the only name. Use it in every sentence. Always.    ║',
    '╚══════════════════════════════════════════════════════════════╝',
    '',
    brief,
  ].join('\n');
}

// ═══════════════════════════════════════════════════════════════════════════
// AUTH GATE SCREEN
// ═══════════════════════════════════════════════════════════════════════════
function AuthGate({ onBypass }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: '#F2F5FF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Inter', sans-serif",
      padding: '24px',
    }}>
      <div style={{ maxWidth: '420px', width: '100%', textAlign: 'center' }}>
        {/* Logo */}
        <div style={{ marginBottom: '32px' }}>
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="20" cy="20" r="20" fill="#1A6AFF"/>
            <path d="M20 8C13.373 8 8 13.373 8 20C8 26.627 13.373 32 20 32C26.627 32 32 26.627 32 20C32 13.373 26.627 8 20 8ZM20 10C25.523 10 30 14.477 30 20C30 25.523 25.523 30 20 30C14.477 30 10 25.523 10 20C10 14.477 14.477 10 20 10Z" fill="white" opacity="0.3"/>
            <circle cx="20" cy="20" r="4" fill="white"/>
            <circle cx="20" cy="12" r="2" fill="white" opacity="0.6"/>
            <circle cx="20" cy="28" r="2" fill="white" opacity="0.6"/>
            <circle cx="12" cy="20" r="2" fill="white" opacity="0.6"/>
            <circle cx="28" cy="20" r="2" fill="white" opacity="0.6"/>
          </svg>
        </div>

        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '28px', fontWeight: '700', letterSpacing: '-0.03em', color: '#0A0A1A', marginBottom: '8px' }}>
          Brandostro
        </div>
        <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '40px', lineHeight: '1.6' }}>
          Your complete brand intelligence platform.
        </div>

        <div style={{ background: '#FFFFFF', borderRadius: '16px', padding: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #E2E8F0', marginBottom: '16px' }}>
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '16px', fontWeight: '600', color: '#0A0A1A', marginBottom: '8px' }}>
            Access Required
          </div>
          <div style={{ fontSize: '13px', color: '#6B7280', lineHeight: '1.6', marginBottom: '24px' }}>
            Please visit brandostro.com to sign in and access your brand dashboard.
          </div>
          <a
            href="https://brandostro.com"
            style={{ display: 'block', height: '48px', lineHeight: '48px', background: 'linear-gradient(135deg,#094BCC,#1A6AFF)', borderRadius: '10px', fontFamily: "'Space Grotesk', sans-serif", fontSize: '14px', fontWeight: '600', color: '#fff', textDecoration: 'none', letterSpacing: '0.02em' }}
          >
            Go to Brandostro →
          </a>
        </div>

        {/* Dev bypass — remove before production */}
        {process.env.NODE_ENV === 'development' && (
          <button
            onClick={onBypass}
            style={{ fontSize: '11px', color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Dev: bypass auth
          </button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ERROR BOUNDARY — catches React crashes, shows fallback instead of black screen
// ─────────────────────────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('Brandostro crash:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight:'100vh', background:'#050C1A', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Inter',sans-serif", padding:'24px' }}>
          <div style={{ textAlign:'center', maxWidth:'420px' }}>
            <div style={{ fontSize:'40px', marginBottom:'16px' }}>⚠️</div>
            <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'20px', fontWeight:'700', color:'#F2F5FF', marginBottom:'8px' }}>
              Something went wrong
            </div>
            <div style={{ fontSize:'14px', color:'rgba(242,245,255,0.4)', lineHeight:'1.6', marginBottom:'24px' }}>
              Brandostro encountered an unexpected error. Your work is safe. Please refresh to continue.
            </div>
            <button onClick={() => window.location.reload()}
              style={{ height:'44px', padding:'0 28px', background:'linear-gradient(135deg,#094BCC,#1A6AFF)', border:'none', borderRadius:'10px', fontFamily:"'Space Grotesk',sans-serif", fontSize:'14px', fontWeight:'600', color:'#fff', cursor:'pointer' }}>
              Refresh →
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}


export default function App() {
  const [screen, setScreen]               = useState('loading');
  const [session, setSession]             = useState(null);
  const [brief, setBrief]                 = useState('');
  const [brandData, setBrandData]         = useState(null);
  const [confirmedName, setConfirmedName] = useState(null);
  const [userProvidedName, setUserProvidedName] = useState(null);
  const [error, setError]                 = useState(null);
  const [savedBrandId, setSavedBrandId]   = useState(null);
  const [sharedLogoUrl, setSharedLogoUrl]               = useState(null);
  const [sharedLogoGallery, setSharedLogoGallery]       = useState([]);
  const [openedBrand, setOpenedBrand]                   = useState(null);
  const [resumeKey, setResumeKey]                       = useState(0);
  const [sharedLogoRegenCount, setSharedLogoRegenCount] = useState(0);
  const [sharedSiteHtml, setSharedSiteHtml]             = useState(null);

  // Ref to track current screen — avoids stale closure in auth listener
  const screenRef = React.useRef('loading');
  const setScreenSafe = (s) => { screenRef.current = s; setScreen(s); };

  // ── Supabase Auth — listen for session changes ───────────────────────────
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, sbSession) => {
        console.log('Auth event:', event, '| Screen:', screenRef.current);
        if (event === 'SIGNED_IN' && sbSession) {
          // Only navigate if user is on auth or loading — never interrupt active session
          const currentScreen = screenRef.current;
          if (currentScreen === 'auth' || currentScreen === 'loading') {
            await loadUserProfile(sbSession);
          } else {
            // Silent token refresh — no navigation
            setSession(prev => prev ? { ...prev, token: sbSession.access_token } : prev);
          }
        } else if (event === 'SIGNED_OUT') {
          setSession(null);
          setScreenSafe('auth');
        } else if (event === 'INITIAL_SESSION') {
          if (sbSession) {
            await loadUserProfile(sbSession);
          } else {
            setScreenSafe('auth');
          }
        } else if (event === 'TOKEN_REFRESHED' && sbSession) {
          setSession(prev => prev ? { ...prev, token: sbSession.access_token } : prev);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const loadUserProfile = async (sbSession) => {
    // Always set a valid session immediately — prevents black screen
    const baseSession = {
      memberId: sbSession.user.id,
      email:    sbSession.user.email,
      name:     sbSession.user.user_metadata?.full_name || sbSession.user.user_metadata?.name || '',
      plan:     'discover',
      token:    sbSession.access_token,
    };

    try {
      const profileRes = await fetch(`${ENGINE_URL}/get-member-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ memberId: sbSession.user.id }),
      });
      if (profileRes.ok) {
        const profileData = await profileRes.json();
        if (profileData.success) {
          baseSession.plan = profileData.plan || 'discover';
          baseSession.name = profileData.fullName || baseSession.name;
        }
      }
    } catch (err) {
      console.warn('Profile fetch failed, using defaults:', err.message);
    }

    setSession(baseSession);
    setScreenSafe('dashboard');
  };

  // Refresh plan from engine — called after purchase or on demand
  const refreshPlan = async () => {
    if (!session?.memberId) return;
    try {
      const res = await fetch(`${ENGINE_URL}/get-member-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ memberId: session.memberId }),
      });
      const data = await res.json();
      if (data.success && data.plan) {
        setSession(prev => prev ? { ...prev, plan: data.plan } : prev);
      }
    } catch {}
  };

  // Detect return from Polar checkout — refresh plan immediately
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('plan_updated') === 'true') {
      window.history.replaceState({}, '', window.location.pathname);
      // Retry plan refresh — webhook can take up to 10s to process
      const tryRefresh = async (attempts) => {
        await refreshPlan();
        if (attempts > 0) setTimeout(() => tryRefresh(attempts - 1), 3000);
      };
      setTimeout(() => tryRefresh(4), 2000);
    }
  }, [session?.memberId]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setScreenSafe('auth');
    setBrandData(null);
    setSharedLogoUrl(null);
    setSharedLogoGallery([]);
    setSharedLogoRegenCount(0);
    setSharedSiteHtml(null);
    setOpenedBrand(null);
    setResumeKey(0);
    window.location.href = 'https://www.brandostro.com';
  };

  const handleAuthSuccess = async (user) => {
    // Called directly on email login — session listener handles the rest
  };

  // ── Auto-save brand after generation ─────────────────────────────────────
  const saveBrand = async (brand, name, logoUrl) => {
    if (!session?.memberId) return;
    try {
      const res = await fetch(`${ENGINE_URL}/save-brand`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: session.token || `direct:${session.memberId}`,
          brandName: name,
          brief,
          brandData: brand,
          logoUrl: logoUrl || null,
          plan: session.plan,
          memberId: session.memberId, // fallback if no token
        }),
      });
      const data = await res.json();
      if (data.success) setSavedBrandId(data.brandId);
    } catch (err) {
      console.error('Auto-save failed:', err);
    }
  };

  // ── Generation limits — plan-scoped, never cumulative across plans ──
  const PLAN_LIMITS = { discover: 1, studio: 5, pro: 50, none: 0 };

  const checkGenerationsRemaining = async () => {
    if (!session?.memberId) return { allowed: true, remaining: 0, used: 0, limit: 0 };
    try {
      const res = await fetch(`${ENGINE_URL}/check-limit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ memberId: session.memberId, plan: session.plan }),
      });
      const data = await res.json();
      return data.success
        ? { allowed: data.allowed, remaining: data.remaining, used: data.used, limit: data.limit }
        : { allowed: true, remaining: 0, used: 0, limit: 0 }; // allow on error
    } catch {
      return { allowed: true, remaining: 0, used: 0, limit: 0 }; // allow on network error
    }
  };

  // ── Step 1: Initial generation ────────────────────────────────────────────
  const handleGenerate = async (userBrief, providedName = null) => {
    // Check generation limit first — plan-scoped, not lifetime total
    const limitCheck = await checkGenerationsRemaining();
    if (!limitCheck.allowed) {
      setError(`You have used all ${limitCheck.limit} ${limitCheck.limit === 1 ? 'generation' : 'generations'} on your ${session?.plan === 'discover' ? 'Discover' : session?.plan === 'studio' ? 'Brand Studio' : 'Brand Pro'} plan. Visit brandostro.com to upgrade.`);
      return;
    }
    setBrief(userBrief);
    setUserProvidedName(providedName || null);
    setScreenSafe('generating');
    setError(null);
    setSavedBrandId(null);
    setSharedLogoUrl(null);
    setSharedLogoGallery([]);
    setSharedLogoRegenCount(0);
    setSharedSiteHtml(null);
    setOpenedBrand(null);
    setResumeKey(0);
    try {
      const generationBrief = providedName
        ? buildAssessmentBrief(userBrief, providedName)
        : userBrief;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 240000); // 4 min timeout
      const res = await fetch(`${ENGINE_URL}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          brief: generationBrief,
          plan: session?.plan || 'discover',
          memberId: session?.memberId,
          token: session?.token,
          generationType: 'names',
        }),
      });
      clearTimeout(timeout);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Generation failed');
      setBrandData(data.brand);
      setScreenSafe('results');
    } catch (err) {
      console.error('Generation failed:', err);
      if (err.name === 'AbortError') {
        setError('Generation is taking longer than expected. Please try again.');
      } else {
        setError('Generation encountered an issue. Please try again.');
      }
      setScreenSafe('intake');
    }
  };

  // ── Step 2: Name confirmed, full brand generation ─────────────────────────
  const handleNameConfirmed = async (selectedName) => {
    setConfirmedName(selectedName);
    setScreenSafe('generating-brand');
    setError(null);
    try {
      const lockedBrief = buildNameLockedBrief(brief, selectedName);
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 300000); // 5 min timeout
      const res = await fetch(`${ENGINE_URL}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({ brief: lockedBrief, plan: session?.plan || 'discover', memberId: session?.memberId, token: session?.token, generationType: 'brand' }),
      });
      clearTimeout(timeout);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Brand generation failed');
      const merged = {
        phase02: brandData?.phase02,
        ...Object.fromEntries(Object.entries(data.brand).filter(([k]) => k !== 'phase02')),
      };
      setBrandData(merged);
      setScreenSafe('results');
      // Auto-save after full generation
      saveBrand(merged, selectedName, null);
    } catch (err) {
      console.error('Brand generation failed:', err);
      setError('A critical error occurred while building the final brand system. Please restart the session.');
      setScreenSafe('intake');
      setBrandData(null);
      setConfirmedName(null);
    }
  };

  // ── Refinement ────────────────────────────────────────────────────────────
  const handleRegenerate = async (phaseKey, direction, currentName) => {
    const isNamePhase = phaseKey === 'phase02';
    const name = currentName || confirmedName;
    const nameBlock = (!isNamePhase && name)
      ? buildNameLockedBrief('', name).split('\n').slice(0, 6).join('\n') + '\n\n'
      : '';
    const augmented = `${nameBlock}${brief}\n\nREFINEMENT DIRECTION for ${phaseKey}: ${direction}`;
    try {
      const res = await fetch(`${ENGINE_URL}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brief: augmented, plan: session?.plan || 'discover', memberId: session?.memberId, token: session?.token }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Refinement failed');
      setBrandData(prev => ({ ...prev, [phaseKey]: data.brand[phaseKey] }));
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message || 'Failed to refine phase' };
    }
  };

  // ── Reset ─────────────────────────────────────────────────────────────────
  const handleReset = () => {
    setScreenSafe('intake');
    setBrief('');
    setBrandData(null);
    setConfirmedName(null);
    setUserProvidedName(null);
    setError(null);
    setSavedBrandId(null);
    setSharedLogoUrl(null);
    setSharedLogoGallery([]);
    setSharedLogoRegenCount(0);
    setSharedSiteHtml(null);
    setOpenedBrand(null);
    setResumeKey(0);
  };


  // ── Render ────────────────────────────────────────────────────────────────
  if (screen === 'loading') {
    return (
      <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#050C1A' }}>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'16px' }}>
          <div style={{ width:'40px', height:'40px', border:'2px solid rgba(26,106,255,0.2)', borderTopColor:'#1A6AFF', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
          <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'13px', color:'rgba(242,245,255,0.3)', letterSpacing:'0.08em' }}>Loading Brandostro...</div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (screen === 'auth') {
    return <Auth onAuthSuccess={handleAuthSuccess} />;
  }

  return (
    <ErrorBoundary>
      {screen === 'intake' && (
        <Intake
          onGenerate={handleGenerate}
          error={error}
          session={session}
          onDashboard={() => setScreenSafe('dashboard')}
          onSignOut={handleSignOut}
          planLimits={PLAN_LIMITS}
        />
      )}
      {screen === 'generating' && <Generation brief={brief} mode="names" />}
      {screen === 'generating-brand' && <Generation brief={brief} mode="brand" confirmedName={confirmedName} />}
      {screen === 'results' && brandData && (
        <Results
          key={resumeKey}
          brand={brandData}
          brief={brief}
          confirmedName={confirmedName}
          userProvidedName={userProvidedName}
          onReset={handleReset}
          onRegenerate={handleRegenerate}
          onNameConfirmed={handleNameConfirmed}
          session={session}
          savedBrandId={savedBrandId}
          initialLogoGallery={openedBrand ? (Array.isArray(openedBrand.logo_gallery) && openedBrand.logo_gallery.length > 0 ? openedBrand.logo_gallery : (openedBrand.logo_url ? [openedBrand.logo_url] : [])) : sharedLogoGallery}
          initialLogoRegenCount={openedBrand ? (openedBrand.logo_regen_count || 0) : sharedLogoRegenCount}
          initialSiteHtml={openedBrand ? (openedBrand.website_html || null) : sharedSiteHtml}
          onLogoSave={(url, gallery, regenCount) => {
            setSharedLogoUrl(url);
            if (gallery) setSharedLogoGallery(gallery);
            if (regenCount !== undefined) setSharedLogoRegenCount(regenCount);
            if (savedBrandId && session?.token) {
              fetch(`${ENGINE_URL}/update-brand`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  token: session.token,
                  brandId: savedBrandId,
                  logoUrl: url,
                  logoGallery: gallery || [url],
                  logoRegenCount: regenCount || 0,
                }),
              }).catch(console.error);
            }
          }}
          onSiteHtmlSave={(html) => {
            setSharedSiteHtml(html);
            if (savedBrandId && session?.token) {
              fetch(`${ENGINE_URL}/update-brand`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  token: session.token,
                  brandId: savedBrandId,
                  websiteHtml: html,
                }),
              }).catch(console.error);
            }
          }}
          onDashboard={() => setScreenSafe('dashboard')}
        />
      )}
      {screen === 'dashboard' && (
        <Dashboard
          key={`dashboard-${savedBrandId || 'empty'}-${session?.plan || 'discover'}`}
          session={session}
          engineUrl={ENGINE_URL}
          onNewBrand={() => setScreenSafe('intake')}
          onOpenBrand={(brand) => {
            setBrandData(brand.brand_data);
            setBrief(brand.brief || '');
            setConfirmedName(brand.brand_name);
            setSavedBrandId(brand.id);
            setOpenedBrand(brand);
            setResumeKey(k => k + 1);
            setSharedLogoUrl(brand.logo_url || null);
            setSharedLogoGallery(Array.isArray(brand.logo_gallery) ? brand.logo_gallery : (brand.logo_url ? [brand.logo_url] : []));
            setSharedLogoRegenCount(brand.logo_regen_count || 0);
            setSharedSiteHtml(brand.website_html || null);
            setScreenSafe('results');
          }}
          onSignOut={handleSignOut}
        />
      )}
    </ErrorBoundary>
  );
}
